import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { approvalService } from '../../../services/approvalService';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const ApprovalStatusBadge = ({ status }) => {
  const statusConfig = {
    pending: {
      label: 'Pendiente',
      icon: 'Clock',
      className: 'bg-warning/10 text-warning border-warning/20'
    },
    approved: {
      label: 'Aprobada',
      icon: 'CheckCircle',
      className: 'bg-success/10 text-success border-success/20'
    },
    rejected: {
      label: 'Rechazada',
      icon: 'XCircle',
      className: 'bg-error/10 text-error border-error/20'
    },
    cancelled: {
      label: 'Cancelada',
      icon: 'Ban',
      className: 'bg-muted/10 text-muted-foreground border-muted/20'
    }
  };

  const config = statusConfig?.[status] || statusConfig?.pending;

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm font-medium ${config?.className}`}>
      <Icon name={config?.icon} size={14} />
      <span>{config?.label}</span>
    </div>
  );
};

const ApprovalTracker = ({ workOrderId }) => {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (workOrderId) {
      fetchApprovals();
    }
  }, [workOrderId]);

  const fetchApprovals = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await approvalService?.getByWorkOrder(workOrderId);
      setApprovals(data || []);
    } catch (err) {
      console.error('Error fetching approvals:', err);
      setError(err?.message || 'Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async (approvalId) => {
    try {
      setSendingEmail(approvalId);
      
      // Send client notification
      await approvalService?.sendClientNotification(approvalId);
      
      // Send sales team notification
      try {
        await approvalService?.notifySalesTeam(approvalId);
      } catch (salesErr) {
        console.error('Error notifying sales team:', salesErr);
        // Don't fail the whole operation if sales notification fails
      }
      
      await fetchApprovals();
    } catch (err) {
      console.error('Error sending email:', err);
      alert(err?.message || 'Error al enviar correo');
    } finally {
      setSendingEmail(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6 text-center">
        <div className="inline-flex items-center gap-2 text-muted-foreground">
          <Icon name="Loader" size={20} className="animate-spin" />
          <span>Cargando solicitudes...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error/10 border border-error/20 rounded-lg p-4">
        <div className="flex items-center gap-2 text-error">
          <Icon name="AlertCircle" size={20} />
          <span className="text-sm">{error}</span>
        </div>
      </div>
    );
  }

  if (approvals?.length === 0) {
    return (
      <div className="bg-muted/50 rounded-lg border border-border p-6 text-center">
        <Icon name="FileQuestion" size={48} className="text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">
          No hay solicitudes de aprobación para esta orden
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-4 md:p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="ListChecks" size={24} className="text-accent" />
        <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
          Historial de Aprobaciones
        </h3>
      </div>

      <div className="space-y-4">
        {approvals?.map((approval) => (
          <div
            key={approval?.id}
            className="bg-muted/30 rounded-lg border border-border p-4 space-y-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <ApprovalStatusBadge status={approval?.status} />
                  {approval?.emailSent && (
                    <div className="inline-flex items-center gap-1 text-xs text-success">
                      <Icon name="Mail" size={12} />
                      <span>Email enviado</span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  Solicitado por: <span className="font-medium text-foreground">{approval?.requestedByProfile?.fullName}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Revisor: <span className="font-medium text-foreground">{approval?.managerProfile?.fullName || 'No asignado'}</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-heading font-bold text-primary">
                  ${parseFloat(approval?.quotationTotal || 0)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {approval?.requestedAt && format(new Date(approval?.requestedAt), 'dd MMM yyyy', { locale: es })}
                </p>
              </div>
            </div>

            {approval?.requestNotes && (
              <div className="bg-white rounded p-3 border border-border">
                <p className="text-xs caption text-muted-foreground mb-1">Notas de Solicitud:</p>
                <p className="text-sm text-foreground">{approval?.requestNotes}</p>
              </div>
            )}

            {approval?.approvalNotes && (
              <div className="bg-white rounded p-3 border border-border">
                <p className="text-xs caption text-muted-foreground mb-1">Notas de Revisión:</p>
                <p className="text-sm text-foreground">{approval?.approvalNotes}</p>
              </div>
            )}

            {approval?.signatureData && (
              <div className="bg-white rounded p-3 border border-border">
                <p className="text-xs caption text-muted-foreground mb-2">Firma Digital:</p>
                <img
                  src={approval?.signatureData}
                  alt="Firma digital"
                  className="h-16 border border-border rounded"
                />
              </div>
            )}

            {approval?.status === 'approved' && !approval?.emailSent && (
              <Button
                variant="default"
                size="sm"
                iconName="Mail"
                iconPosition="left"
                onClick={() => handleSendEmail(approval?.id)}
                loading={sendingEmail === approval?.id}
                disabled={sendingEmail === approval?.id}
                fullWidth
              >
                Enviar Notificación al Cliente
              </Button>
            )}

            {approval?.reviewedAt && (
              <p className="text-xs text-muted-foreground text-right">
                Revisado: {format(new Date(approval?.reviewedAt), 'dd MMM yyyy HH:mm', { locale: es })}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ApprovalTracker;